/**
 * Sistema de Alertas Globales - Tránsito de Cota
 * @author carlos rodriguez
 * @since 2025
 * 
 * Sistema reutilizable de alertas usando SweetAlert2
 * Configuración centralizada para mantener consistencia en toda la aplicación
 */

// ============================================
// CONFIGURACIÓN GLOBAL DE ALERTAS
// ============================================

const ALERT_CONFIG = {
    // Configuración base compartida por todas las alertas
    base: {
        timer: 3000,
        showConfirmButton: false,
        customClass: {
            popup: 'swal2-border-radius',
            confirmButton: 'swal2-btn-confirm',
            cancelButton: 'swal2-btn-cancel'
        }
    },
    
    // Configuración para alertas de confirmación
    confirm: {
        showCancelButton: true,
        showConfirmButton: true,
        reverseButtons: true,
        confirmButtonText: 'Sí, continuar',
        cancelButtonText: 'Cancelar'
    },
    
    // Iconos por tipo
    icons: {
        success: 'success',
        error: 'error',
        warning: 'warning',
        info: 'info',
        question: 'question'
    }
};

// ============================================
// FUNCIÓN PRINCIPAL: showAlert()
// ============================================

/**
 * Mostrar alerta global con configuración personalizable
 * @param {Object} options - Opciones de configuración
 * @param {string} options.type - Tipo de alerta: 'success', 'error', 'warning', 'info', 'confirm'
 * @param {string} options.title - Título de la alerta
 * @param {string} options.text - Texto descriptivo (opcional)
 * @param {number} options.timer - Tiempo de cierre automático en ms (opcional, default: 3000)
 * @param {boolean} options.showConfirmButton - Mostrar botón confirmar (opcional)
 * @param {Function} options.onConfirm - Callback cuando se confirma (solo para tipo 'confirm')
 * @param {Function} options.onCancel - Callback cuando se cancela (opcional)
 * @param {string} options.confirmButtonText - Texto del botón confirmar (opcional)
 * @param {string} options.cancelButtonText - Texto del botón cancelar (opcional)
 * @returns {Promise} - Promise de SweetAlert2
 * 
 * @example
 * // Alerta de éxito simple
 * showAlert({
 *     type: 'success',
 *     title: '¡Operación exitosa!',
 *     text: 'Los datos se guardaron correctamente'
 * });
 * 
 * @example
 * // Alerta de confirmación con callback
 * showAlert({
 *     type: 'confirm',
 *     title: '¿Confirmar eliminación?',
 *     text: 'Esta acción no se puede deshacer',
 *     onConfirm: () => { console.log('Usuario confirmó'); },
 *     onCancel: () => { console.log('Usuario canceló'); }
 * });
 */
window.showAlert = function(options = {}) {
    // Validar que exista SweetAlert2
    if (typeof Swal === 'undefined') {
        //console.error('❌ SweetAlert2 no está disponible. Asegúrate de importarlo en app.js');
        return Promise.reject('SweetAlert2 no disponible');
    }

    // Valores por defecto
    const defaults = {
        type: 'info',
        title: '',
        text: '',
        timer: ALERT_CONFIG.base.timer,
        showConfirmButton: ALERT_CONFIG.base.showConfirmButton,
        confirmButtonText: 'Aceptar',
        cancelButtonText: 'Cancelar',
        onConfirm: null,
        onCancel: null
    };

    // Combinar opciones con valores por defecto
    const config = { ...defaults, ...options };

    // Configuración de SweetAlert2
    let swalConfig = {
        icon: ALERT_CONFIG.icons[config.type] || 'info',
        title: config.title,
        text: config.text,
        timer: config.timer,
        showConfirmButton: config.showConfirmButton,
        customClass: ALERT_CONFIG.base.customClass
    };

    // Si es tipo 'confirm', aplicar configuración especial
    if (config.type === 'confirm') {
        swalConfig = {
            ...swalConfig,
            icon: 'question',
            timer: null, // Sin cierre automático
            ...ALERT_CONFIG.confirm,
            confirmButtonText: config.confirmButtonText,
            cancelButtonText: config.cancelButtonText
        };
    }

    // Mostrar alerta y manejar callbacks
    return Swal.fire(swalConfig).then((result) => {
        if (result.isConfirmed && typeof config.onConfirm === 'function') {
            config.onConfirm();
        } else if (result.isDismissed && typeof config.onCancel === 'function') {
            config.onCancel();
        }
        return result;
    });
};

// ============================================
// FUNCIONES DE ATAJO (Shortcuts)
// ============================================

/**
 * Alerta de éxito (shortcut)
 * @param {string} title - Título
 * @param {string} text - Texto descriptivo (opcional)
 * @param {number} timer - Tiempo de cierre (opcional)
 */
window.alertSuccess = function(title, text = '', timer = 3000) {
    return showAlert({ type: 'success', title, text, timer });
};

/**
 * Alerta de error (shortcut)
 * @param {string} title - Título
 * @param {string} text - Texto descriptivo (opcional)
 * @param {number} timer - Tiempo de cierre (opcional)
 */
window.alertError = function(title, text = '', timer = 3000) {
    return showAlert({ type: 'error', title, text, timer });
};

/**
 * Alerta de advertencia (shortcut)
 * @param {string} title - Título
 * @param {string} text - Texto descriptivo (opcional)
 * @param {number} timer - Tiempo de cierre (opcional)
 */
window.alertWarning = function(title, text = '', timer = 3000) {
    return showAlert({ type: 'warning', title, text, timer });
};

/**
 * Alerta de información (shortcut)
 * @param {string} title - Título
 * @param {string} text - Texto descriptivo (opcional)
 * @param {number} timer - Tiempo de cierre (opcional)
 */
window.alertInfo = function(title, text = '', timer = 3000) {
    return showAlert({ type: 'info', title, text, timer });
};

/**
 * Alerta de confirmación (shortcut)
 * @param {string} title - Título
 * @param {string} text - Texto descriptivo (opcional)
 * @param {Function} onConfirm - Callback al confirmar
 * @param {Function} onCancel - Callback al cancelar (opcional)
 */
window.alertConfirm = function(title, text = '', onConfirm = null, onCancel = null) {
    return showAlert({ 
        type: 'confirm', 
        title, 
        text, 
        onConfirm, 
        onCancel 
    });
};

// ============================================
// MANEJO DE MENSAJES DE SESIÓN
// ============================================

/**
 * Procesar mensajes flash de Laravel automáticamente
 * Lee mensajes de sesión y sessionStorage para mostrar alertas
 */
window.processFlashMessages = function() {
    // 1. Verificar sessionStorage (para AJAX)
    const successMsg = sessionStorage.getItem('success_message');
    const errorMsg = sessionStorage.getItem('error_message');
    const warningMsg = sessionStorage.getItem('warning_message');
    const infoMsg = sessionStorage.getItem('info_message');

    if (successMsg) {
        alertSuccess('¡Éxito!', successMsg);
        sessionStorage.removeItem('success_message');
    }
    if (errorMsg) {
        alertError('¡Error!', errorMsg);
        sessionStorage.removeItem('error_message');
    }
    if (warningMsg) {
        alertWarning('Atención', warningMsg);
        sessionStorage.removeItem('warning_message');
    }
    if (infoMsg) {
        alertInfo('Información', infoMsg);
        sessionStorage.removeItem('info_message');
    }

    // 2. Verificar elementos en el DOM (para mensajes flash de Laravel)
    // Esto se integra con partials.alerts existente
    const flashSuccess = document.querySelector('[data-flash-success]');
    const flashError = document.querySelector('[data-flash-error]');
    const flashWarning = document.querySelector('[data-flash-warning]');
    const flashInfo = document.querySelector('[data-flash-info]');

    if (flashSuccess) {
        alertSuccess('¡Éxito!', flashSuccess.getAttribute('data-flash-success'));
        flashSuccess.remove();
    }
    if (flashError) {
        alertError('¡Error!', flashError.getAttribute('data-flash-error'));
        flashError.remove();
    }
    if (flashWarning) {
        alertWarning('Atención', flashWarning.getAttribute('data-flash-warning'));
        flashWarning.remove();
    }
    if (flashInfo) {
        alertInfo('Información', flashInfo.getAttribute('data-flash-info'));
        flashInfo.remove();
    }
};

// Auto-ejecutar al cargar el DOM
document.addEventListener('DOMContentLoaded', processFlashMessages);

// ============================================
// UTILIDADES ADICIONALES
// ============================================

/**
 * Almacenar mensaje en sessionStorage para mostrar después de redirect
 * @param {string} type - Tipo: 'success', 'error', 'warning', 'info'
 * @param {string} message - Mensaje a almacenar
 */
window.storeAlertMessage = function(type, message) {
    sessionStorage.setItem(`${type}_message`, message);
};

/**
 * Mostrar alerta de carga (loading)
 * @param {string} title - Título del loading
 * @param {string} text - Texto descriptivo (opcional)
 * @returns {Object} - Instancia de Swal para poder cerrarla después
 */
window.showLoading = function(title = 'Procesando...', text = 'Por favor espera') {
    return Swal.fire({
        title,
        html: text,
        timerProgressBar: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        customClass: ALERT_CONFIG.base.customClass,
        didOpen: () => {
            Swal.showLoading();
        }
    });
};

/**
 * Cerrar alerta de carga activa
 */
window.hideLoading = function() {
    Swal.close();
};

// ============================================
// VALIDACIÓN DE ERRORES (para formularios)
// ============================================

/**
 * Mostrar errores de validación de Laravel (422)
 * @param {Object} errors - Objeto de errores del response JSON
 */
window.showValidationErrors = function(errors = {}) {
    if (Object.keys(errors).length === 0) {
        alertError('Error de validación', 'Por favor revisa los campos del formulario');
        return;
    }

    let mensajeErrores = 'Por favor completa los siguientes campos:\n\n';
    Object.entries(errors).forEach(([campo, mensajes]) => {
        mensajeErrores += `• ${mensajes[0]}\n`;
    });

    Swal.fire({
        icon: 'warning',
        title: 'Validación',
        html: mensajeErrores.replace(/\n/g, '<br>'),
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#f59e0b',
        customClass: ALERT_CONFIG.base.customClass
    });
};

// Solo mostrar en desarrollo
if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    //console.log('✅ Sistema de Alertas Globales inicializado');
}
